import { useCallback } from 'react';

import { plural, t, Trans } from '@lingui/macro';

import toColorString from 'polished/lib/color/toColorString';
import color from '@decisiv/design-tokens/lib/color';

import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import Download from '@decisiv/iconix/lib/components/Download';
import ExclamationTriangleF from '@decisiv/iconix/lib/components/ExclamationTriangleF';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import { useBulkAddAssetContext } from '../context/BulkAddAssetContext';

const SUCCESS_COLOR = toColorString(color.status.success.medium);
const DANGER_COLOR = toColorString(color.status.danger.medium);

const StepThree = () => {
  const { savedState } = useBulkAddAssetContext();

  const {
    assetsAddedCount,
    assetsFailedCount,
    depotsAddedCount,
    depotsFailedCount,
    reportUrl,
    stillProcessing,
  } = savedState.summary;

  const isFullFailure = assetsAddedCount === 0;

  const handleDownloadReportClick = useCallback(() => {
    /* istanbul ignore next -- @preserve */
    if (!reportUrl) return;

    window.location.assign(reportUrl);
  }, [reportUrl]);

  return (
    <Flex flex={1} flexDirection="column">
      <H3>
        <Trans>Summary</Trans>
      </H3>
      <Flex justifyContent="space-between" alignItems="center" marginTop={1.5}>
        {stillProcessing ? (
          <P shade={1}>
            <Trans>
              Your upload is still processing. Check back later to download the
              results report.
            </Trans>
          </P>
        ) : (
          <Flex flexDirection="column" style={{ rowGap: '8px' }}>
            {assetsAddedCount > 0 && (
              <Flex alignItems="center" style={{ gap: '8px' }}>
                <CheckCircleF size="small" color={SUCCESS_COLOR} />
                <P shade={1}>
                  {t`${plural(assetsAddedCount, {
                    one: `${assetsAddedCount} Asset`,
                    other: `${assetsAddedCount} Assets`,
                  })} successfully added to ${plural(depotsAddedCount, {
                    one: `${depotsAddedCount} Depot`,
                    other: `${depotsAddedCount} Depots`,
                  })}.`}
                </P>
              </Flex>
            )}
            {assetsFailedCount > 0 && (
              <Flex alignItems="center" style={{ gap: '8px' }}>
                <ExclamationTriangleF size="small" color={DANGER_COLOR} />
                <P shade={1}>
                  {isFullFailure
                    ? plural(assetsFailedCount, {
                        one: `The asset failed to be added to the Depots.`,
                        other: `All ${assetsFailedCount} assets failed to be added to the Depots.`,
                      })
                    : t`${plural(assetsFailedCount, {
                        one: `${assetsFailedCount} Asset`,
                        other: `${assetsFailedCount} Assets`,
                      })} failed to be added to ${plural(depotsFailedCount, {
                        one: `${depotsFailedCount} Depot`,
                        other: `${depotsFailedCount} Depots`,
                      })}.`}
                </P>
              </Flex>
            )}
          </Flex>
        )}
        <Button
          text={t`Download Report`}
          size="small"
          kind="secondary"
          icon={Download}
          disabled={!reportUrl || stillProcessing}
          onClick={handleDownloadReportClick}
        />
      </Flex>
    </Flex>
  );
};

export default StepThree;
